import {apiFetch} from '../../../shared/apiFetch';
import {API_CONFIG, ERROR_MESSAGES} from '../../config';

class ProfileService {
    constructor() {
        this.baseUrl = API_CONFIG.PROXY_URL;
        this.timeout = API_CONFIG.TIMEOUT;
    }

    createTimeoutController(timeoutMs = this.timeout) {
        if (typeof AbortController === 'undefined') return null;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        return {
            signal: controller.signal,
            cancel: () => clearTimeout(timeoutId),
        };
    }

    async getProfiles(accountId) {
        if (!accountId || typeof fetch !== 'function') return [];

        const timeoutController = this.createTimeoutController();

        try {
            const response = await apiFetch(
                `${this.baseUrl}/profiles/${encodeURIComponent(accountId)}`,
                {
                    method: 'GET',
                    headers: {'Content-Type': 'application/json'},
                    signal: timeoutController?.signal,
                },
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch profiles: ${response.statusText}`);
            }

            const data = await response.json();
            if (!Array.isArray(data)) return [];

            return data.map((profile) => ({
                ...profile,
                settings: this.parseSettings(profile.settings || profile.profileSettings),
            }));
        } catch (error) {
            throw this.handleError(error, ERROR_MESSAGES.PROFILE_LOAD_FAILED);
        } finally {
            timeoutController?.cancel();
        }
    }

    async createProfile(profile) {
        if (typeof fetch !== 'function') {
            throw new Error(ERROR_MESSAGES.PROFILE_SAVE_FAILED);
        }

        const timeoutController = this.createTimeoutController();

        try {
            const response = await apiFetch(`${this.baseUrl}/profiles`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(profile),
                signal: timeoutController?.signal,
            });

            if (!response.ok) {
                throw new Error(`Failed to create profile: ${response.statusText}`);
            }

            const data = await response.json();
            return {
                ...data,
                settings: this.parseSettings(data.settings || profile.settings),
            };
        } catch (error) {
            throw this.handleError(error, ERROR_MESSAGES.PROFILE_SAVE_FAILED);
        } finally {
            timeoutController?.cancel();
        }
    }

    async updateProfile(id, payload) {
        if (!id) {
            throw new Error('Profile id is required to update profile');
        }

        if (typeof fetch !== 'function') {
            throw new Error(ERROR_MESSAGES.PROFILE_SAVE_FAILED);
        }

        const timeoutController = this.createTimeoutController();

        try {
            const response = await apiFetch(`${this.baseUrl}/profiles/${encodeURIComponent(id)}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload),
                signal: timeoutController?.signal,
            });

            if (!response.ok) {
                throw new Error(`Failed to update profile: ${response.statusText}`);
            }

            const data = await response.json();
            return {
                ...data,
                settings: this.parseSettings(data.settings || payload.settings),
            };
        } catch (error) {
            throw this.handleError(error, ERROR_MESSAGES.PROFILE_SAVE_FAILED);
        } finally {
            timeoutController?.cancel();
        }
    }

    async deleteProfile(id) {
        if (!id) {
            throw new Error('Profile id is required to delete profile');
        }

        if (typeof fetch !== 'function') {
            throw new Error(ERROR_MESSAGES.PROFILE_DELETE_FAILED);
        }

        const timeoutController = this.createTimeoutController();

        try {
            const response = await apiFetch(`${this.baseUrl}/profiles/${encodeURIComponent(id)}`, {
                method: 'DELETE',
                headers: {'Content-Type': 'application/json'},
                signal: timeoutController?.signal,
            });

            if (!response.ok && response.status !== 204) {
                throw new Error(`Failed to delete profile: ${response.statusText}`);
            }

            return true;
        } catch (error) {
            throw this.handleError(error, ERROR_MESSAGES.PROFILE_DELETE_FAILED);
        } finally {
            timeoutController?.cancel();
        }
    }

    parseSettings(settings) {
        if (!settings) return undefined;
        if (typeof settings === 'object') return settings;

        try {
            return JSON.parse(settings);
        } catch {
            return undefined;
        }
    }

    handleError(error, fallbackMessage) {
        if (error?.name === 'AbortError') {
            return new Error(ERROR_MESSAGES.NETWORK_ERROR);
        }

        if (error?.message?.toLowerCase().includes('fetch')) {
            return new Error(ERROR_MESSAGES.NETWORK_ERROR);
        }

        return error instanceof Error ? error : new Error(fallbackMessage);
    }
}

export const profileService = new ProfileService();
